import React, { useContext, useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { DataContext } from "../../newComponents/context/DataContext";
import Pagination from "../../newComponents/utilities/Pagination";
import LoadingScreen from "../../newComponents/utilities/LoadingScreen";
import Select from "react-select";
import { FaChevronDown, FaChevronUp, FaFilter, FaUserCircle, FaEye, FaSortAmountDown, FaSortAmountUp } from "react-icons/fa";

const ParticipantTable = () => {
  const { participant, eventData, allEventNames, allEventsSummary, usersInfo, loading } = useContext(DataContext);

  // Filter inputs + UI state — these are the only fields that need to be
  // actual `useState`. Everything derived from props/context lives in
  // `useMemo` below; mixing the two used to cause a cascade where each
  // async DataContext fetch re-fired filter recomputation via setState,
  // producing jerky multi-render UX while the page initially loaded.
  const [filters, setFilters] = useState({
    event_name: "",
    city: "",
    state: "",
    pincode: "",
    age_category: "",
    gender_category: "",
    followers_count_category: "",
    generic_category: "",
    international: "",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [passphrase, setPassphrase] = useState("");
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(true);
  const [sortOrder, setSortOrder] = useState("desc"); // desc = newest first
  const navigate = useNavigate();

  // Build a lookup map from bare numeric ID → registration data from userinformation.
  // Covers the case where phone/OTP users have no IG data and no OAuth name — their
  // real name lives in userinformation (filled during the registration form steps).
  const userInfoMap = useMemo(() => {
    const map = {};
    const list = Array.isArray(usersInfo) ? usersInfo : (usersInfo?.data || []);
    list.forEach((u) => {
      if (!u?.user_id) return;
      // user_id is "facebook:123..." or "instagram:123..." — strip the prefix
      const bare = u.user_id.includes(":") ? u.user_id.split(":")[1] : u.user_id;
      if (bare) map[bare] = u;
    });
    return map;
  }, [usersInfo]);

  // Normalise participants once. The DataContext sometimes hands us
  // `{participants:[...]}` and sometimes a flat array.
  const participants = useMemo(() => {
    if (!participant) return [];
    if (Array.isArray(participant?.participants)) return participant.participants;
    if (Array.isArray(participant)) return participant;
    return [];
  }, [participant]);

  // Filter options recomputed only when the underlying lists change — no
  // intermediate setState, so the page does not re-render mid-derivation.
  const availableFilters = useMemo(() => {
    const data = participants;
    if (!data.length) return {};
    
    const eventParticipantCounts = {};
    const citySet = new Set();
    const stateSet = new Set();
    const pincodeSet = new Set();
    const ageSet = new Set();
    const genderSet = new Set();
    const followerSet = new Set();
    const genericSet = new Set();

    // Single pass over all participants and details to collect filter options
    data.forEach(participant => {
      participant.participationDetails?.forEach(detail => {
        if (detail.event_id) {
          eventParticipantCounts[detail.event_id] = (eventParticipantCounts[detail.event_id] || 0) + 1;
        }
        if (detail.city?.String) citySet.add(detail.city.String);
        if (detail.state?.String) stateSet.add(detail.state.String);
        if (detail.pincode?.String) pincodeSet.add(detail.pincode.String);
        if (detail.age_category?.String) ageSet.add(detail.age_category.String);
        if (detail.gender_category?.String) genderSet.add(detail.gender_category.String);
        if (detail.followers_count_category?.String) followerSet.add(detail.followers_count_category.String);
        if (detail.generic_category?.String) genericSet.add(detail.generic_category.String);
      });
    });
    
    // Create event options with participant counts
    const eventOptions = allEventsSummary.map(event => {
      const eventId = event.event_id;
      const eventName = event.event_name;
      const count = eventId ? eventParticipantCounts[eventId] || 0 : 0;
      return {
        value: eventName,
        label: `${eventName} (${count})`
      };
    });
    
    const options = {
      event_name: eventOptions,
      city: [...citySet].map(option => ({ value: option, label: option })),
      state: [...stateSet].map(option => ({ value: option, label: option })),
      pincode: [...pincodeSet].map(option => ({ value: option, label: option })),
      age_category: [...ageSet].map(option => ({ value: option, label: option })),
      gender_category: [...genderSet].map(option => ({ value: option, label: option })),
      followers_count_category: [...followerSet].map(option => ({ value: option, label: option })),
      generic_category: [...genericSet].map(option => ({ value: option, label: option })),
      international: [
        { value: "true", label: "International" },
        { value: "false", label: "Domestic" }
      ],
    };
    return options;
  }, [participants, allEventsSummary]);

  // Handle filter changes — also reset to page 1 so results are immediately visible
  const handleFilterChange = (selectedOption, actionMeta) => {
    const { name } = actionMeta;
    const value = selectedOption ? selectedOption.value : "";
    setFilters({ ...filters, [name]: value });
    setCurrentPage(1);
  };

  // Clear all filters
  const clearAllFilters = () => {
    setFilters({
      event_name: "",
      city: "",
      state: "",
      pincode: "",
      age_category: "",
      gender_category: "",
      followers_count_category: "",
      generic_category: "",
      international: "",
    });
    setCurrentPage(1);
  };

  // Apply filters then sort — pure derivation, no setState dance
  const filteredParticipants = useMemo(() => {
    if (!participants.length) return [];
    let filtered = participants;

    if (filters.event_name) {
      const event = allEventsSummary?.find((e) => e.event_name === filters.event_name);
      // Use String() to guard against int/string type mismatch between API sources
      const eventId = event?.event_id != null ? String(event.event_id) : null;
      if (eventId) {
        filtered = filtered.filter((part) =>
          part.participationDetails?.some((detail) => String(detail.event_id) === eventId)
        );
      }
    }
    if (filters.city) {
      filtered = filtered.filter((part) =>
        part.participationDetails?.some(
          (d) => (d.city?.String ?? d.city) === filters.city
        )
      );
    }
    if (filters.state) {
      filtered = filtered.filter((part) =>
        part.participationDetails?.some(
          (d) => (d.state?.String ?? d.state) === filters.state
        )
      );
    }
    if (filters.pincode) {
      filtered = filtered.filter((part) =>
        part.participationDetails?.some(
          (d) => (d.pincode?.String ?? d.pincode) === filters.pincode
        )
      );
    }
    if (filters.age_category) {
      filtered = filtered.filter((part) =>
        part.participationDetails?.some(
          (d) => (d.age_category?.String ?? d.age_category) === filters.age_category
        )
      );
    }
    if (filters.gender_category) {
      filtered = filtered.filter((part) =>
        part.participationDetails?.some(
          (d) => (d.gender_category?.String ?? d.gender_category) === filters.gender_category
        )
      );
    }
    if (filters.followers_count_category) {
      filtered = filtered.filter((part) =>
        part.participationDetails?.some(
          (d) =>
            (d.followers_count_category?.String ?? d.followers_count_category) ===
            filters.followers_count_category
        )
      );
    }
    if (filters.generic_category) {
      filtered = filtered.filter((part) =>
        part.participationDetails?.some(
          (d) => (d.generic_category?.String ?? d.generic_category) === filters.generic_category
        )
      );
    }
    if (filters.international !== "") {
      const isInternational = filters.international === "true";
      filtered = filtered.filter((part) =>
        part.participationDetails?.some((detail) => detail.international === isInternational)
      );
    }

    // Sort by joining date
    filtered = [...filtered].sort((a, b) => {
      const ta = a.joiningTime ? new Date(a.joiningTime).getTime() : 0;
      const tb = b.joiningTime ? new Date(b.joiningTime).getTime() : 0;
      return sortOrder === "desc" ? tb - ta : ta - tb;
    });

    return filtered;
  }, [filters, participants, allEventsSummary, sortOrder]);

  // Pagination Logic
  const totalPages = Math.ceil((filteredParticipants?.length || 0) / pageSize);
  const paginatedParticipants = (filteredParticipants || []).slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  // Handle Row Click
  const handleRowClick = (participant) => {
    setSelectedParticipant(participant);
    navigate(
      `/participants/${participant.userId}
      /${participant.instagramBusinessAccountId}
      / ${participant.isiguser}`,
      {
        state: {
          participant,
        },
      }
    );
  };

  // Handle Passphrase Submission
  const handlePassphraseSubmit = () => {
    if (passphrase.trim() !== "" && selectedParticipant) {
      navigate(
        `/participants/${selectedParticipant.userId}
        /${selectedParticipant.instagramBusinessAccountId}`,
        {
          state: {
            participant: selectedParticipant,
          },
        }
      );
      setIsModalOpen(false);
      setPassphrase("");
    }
  };

  // Mirror Events page: show full-screen loader while the context is still
  // hydrating, otherwise the empty filters + "No participant data available"
  // message look like a broken state.
  if (loading && participants.length === 0) return <LoadingScreen />;

  return (
    <main className="lg:mr-8">
      {/* Filters Section */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm mb-6 overflow-hidden">
        <div 
          className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
          onClick={() => setIsFiltersExpanded(!isFiltersExpanded)}
        >
          <div className="flex items-center gap-3">
            <FaFilter className="text-gray-600" />
            <h3 className="text-lg font-semibold text-gray-800">Filters</h3>
            <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
              {Object.values(filters).filter(v => v !== "").length} active
            </span>
          </div>
          <div className="flex items-center gap-2">
            {Object.values(filters).some(v => v !== "") && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  clearAllFilters();
                }}
                className="text-sm text-red-600 hover:text-red-800 font-medium"
              >
                Clear All
              </button>
            )}
            {isFiltersExpanded ? (
              <FaChevronUp className="text-gray-600" />
            ) : (
              <FaChevronDown className="text-gray-600" />
            )}
          </div>
        </div>
        
        {isFiltersExpanded && (
          <div className="px-4 pb-4 border-t border-gray-100">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
              {Object.keys(filters).map((key) => {
                const placeholder = `Select ${key.replace("_", " ").replace(/\b\w/g, l => l.toUpperCase())}`;
                const currentValue = availableFilters[key]?.find(option => option.value === filters[key]);
                
                return (
                  <div key={key} className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700 capitalize">
                      {key.replace("_", " ")}
                    </label>
                    <Select
                      name={key}
                      value={currentValue || null}
                      onChange={handleFilterChange}
                      options={availableFilters[key] || []}
                      placeholder={placeholder}
                      isClearable
                      className="react-select-container"
                      classNamePrefix="react-select"
                      menuPortalTarget={document.body}
                      styles={{
                        control: (provided, state) => ({
                          ...provided,
                          minHeight: '44px',
                          borderColor: state.isFocused ? '#3B82F6' : '#D1D5DB',
                          boxShadow: state.isFocused ? '0 0 0 1px #3B82F6' : 'none',
                          '&:hover': {
                            borderColor: '#9CA3AF',
                          },
                          borderRadius: '8px',
                          fontSize: '14px',
                        }),
                        option: (provided, state) => ({
                          ...provided,
                          backgroundColor: state.isSelected ? '#3B82F6' : state.isFocused ? '#EBF4FF' : 'white',
                          color: state.isSelected ? 'white' : '#374151',
                          padding: '10px 12px',
                          fontSize: '14px',
                          cursor: 'pointer',
                        }),
                        singleValue: (provided) => ({
                          ...provided,
                          color: '#374151',
                          fontSize: '14px',
                        }),
                        placeholder: (provided) => ({
                          ...provided,
                          color: '#9CA3AF',
                          fontSize: '14px',
                        }),
                        menu: (provided) => ({
                          ...provided,
                          borderRadius: '8px',
                          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
                          border: '1px solid #E5E7EB',
                          zIndex: 9999,
                        }),
                        menuList: (provided) => ({
                          ...provided,
                          padding: '4px',
                          maxHeight: '200px',
                        }),
                        menuPortal: (provided) => ({
                          ...provided,
                          zIndex: 9999,
                        }),
                      }}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Participants Table */}
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-gray-800">
          Total Participants: {filteredParticipants?.length || 0}
        </h2>
        <button
          onClick={() => { setSortOrder((o) => (o === "desc" ? "asc" : "desc")); setCurrentPage(1); }}
          className="flex items-center gap-2 text-sm text-gray-600 hover:text-blue-600 border border-gray-300 hover:border-blue-400 px-3 py-1.5 rounded-lg transition"
          title={sortOrder === "desc" ? "Newest first — click for oldest first" : "Oldest first — click for newest first"}
        >
          {sortOrder === "desc" ? <FaSortAmountDown /> : <FaSortAmountUp />}
          {sortOrder === "desc" ? "Newest first" : "Oldest first"}
        </button>
      </div>
      <div className="border border-gray-300 rounded-lg overflow-x-auto my-8">
        <table className="min-w-full">
          <thead>
            <tr className="bg-gray-100 text-gray-800 text-left">
              <th className="p-4">Profile Pic.</th>
              <th className="p-4">Insta User Name</th>
              <th className="p-4">Followers</th>
              <th className="p-4">Name</th>
              <th className="p-4">Date Of Joining</th>
              <th className="p-4">Events Participated</th>
              <th className="p-4 text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            {paginatedParticipants && paginatedParticipants.length > 0 ? (
              paginatedParticipants.map((part, index) => (
                <tr
                  key={index}
                  className="hover:bg-gray-100 transition duration-200 border-b ease-in-out"
                >
                  <td className="p-4">
                    {(() => {
                      const username = part.instagramUser?.username || "";
                      const uid = String(part.userId || part.facebookId || "");
                      const bizId =
                        part.instagramBusinessAccountId ||
                        part.instagramUser?.id ||
                        (uid.includes(":") ? uid.split(":")[1] : uid);
                      const igUrl = username
                        ? `https://instagram.com/${username}`
                        : bizId
                        ? `https://www.instagram.com/_u/${bizId}/`
                        : null;
                      const avatar = part.instagramUser?.profile_picture_url ? (
                        <img
                          src={part.instagramUser.profile_picture_url}
                          alt="profile-pic"
                          className="w-12 h-12 rounded-full border border-gray-300 shadow-sm hover:scale-105 hover:shadow-lg transition-transform object-cover"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-full border border-gray-300 bg-gray-100 flex items-center justify-center shadow-sm hover:scale-105 hover:shadow-lg transition-transform">
                          <FaUserCircle className="w-8 h-8 text-gray-400" />
                        </div>
                      );
                      return igUrl ? (
                        <a
                          href={igUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Open Instagram profile"
                        >
                          {avatar}
                        </a>
                      ) : (
                        avatar
                      );
                    })()}
                  </td>
                  <td className="p-4">
                    {(() => {
                      // 1) IG username known → direct link
                      if (part.instagramUser?.username) {
                        return (
                          <a
                            href={`https://instagram.com/${part.instagramUser.username}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-500 hover:text-blue-700 underline"
                          >
                            @{part.instagramUser.username}
                          </a>
                        );
                      }
                      // 2) No IG handle on record. The numeric Instagram id is
                      // app-scoped and cannot be resolved to a @handle without
                      // the user's own (now-dead) token — so instead of a
                      // useless "View profile" link, identify them by the real
                      // name / email / phone we captured at registration.
                      const uid = String(part.userId || part.facebookId || "");
                      const bareId2 = uid.split(":").pop();
                      const ui2 = userInfoMap[bareId2] || {};
                      const name = part.name || ui2.name || "";
                      const email = part.email || ui2.email || "";
                      const phone = part.mobile_no || ui2.mobile_no || "";
                      const isPhoneUser = !part.isiguser &&
                        !part.instagramBusinessAccountId && !part.instagramUser?.id;
                      if (name || email || phone) {
                        return (
                          <div className="flex flex-col leading-tight">
                            <span className="text-gray-700 text-sm">
                              {name || email || phone}
                            </span>
                            <span className="text-[10px] text-gray-400">
                              {isPhoneUser ? "phone signup · no IG" : "no IG handle on record"}
                            </span>
                          </div>
                        );
                      }
                      return <span className="text-gray-400 text-sm">No IG</span>;
                    })()}
                  </td>
                  <td className="p-4">{(part.instagramUser?.followers_count ?? 0).toLocaleString()}</td>
                  <td className="p-4">
                    {(() => {
                      const bareId = String(part.userId || part.facebookId || "").split(":").pop();
                      const ui = userInfoMap[bareId] || {};
                      return (
                        part.instagramUser?.name ||
                        part.name ||
                        ui.name ||
                        part.email ||
                        ui.email ||
                        <span className="text-gray-400">—</span>
                      );
                    })()}
                  </td>
                  <td className="p-4">
                    {part.joiningTime ? new Date(part.joiningTime).toLocaleDateString("en-GB") : 'N/A'}
                  </td>
                  <td className="p-4">{part.participationDetails?.length || 0}</td>
                  <td className="p-4 text-center">
                    <Link
                      to={`/participant-detail/${part.userId || part.facebookId}`}
                      className="inline-flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg transition duration-200 shadow-md hover:shadow-lg"
                      title="View participant details"
                    >
                      <FaEye className="w-4 h-4" />
                      View
                    </Link>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="p-8 text-center text-gray-500">
                  No participant data available.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Component */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        pageSize={pageSize}
      />

      {/* Passphrase Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded p-6 w-full max-w-sm">
            <h3 className="text-lg mb-4">Enter Passphrase</h3>
            <input
              type="text"
              value={passphrase}
              onChange={(e) => setPassphrase(e.target.value)}
              placeholder="Passphrase"
              className="border w-full px-3 py-2 rounded mb-4"
            />
            <div className="flex justify-between">
              <button
                onClick={() => setIsModalOpen(false)}
                className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
              >
                Cancel
              </button>
              <button
                onClick={handlePassphraseSubmit}
                className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default ParticipantTable;
